import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './styles/tokens.css'
import './styles/skins.css'
import './store/skinStore'  // aplica data-skin al DOM en el arranque
import App from './App'
import IncidentView from './views/IncidentView'
import GuardView from './views/GuardView'

// ─── Auto-update agresivo del PWA ───────────────────────────────────────────
// CAD operacional: el operador NUNCA debe quedar con una versión vieja del
// frontend. Cuando el SW detecta un bundle nuevo en el server, lo activa
// inmediatamente y recarga la app sin preguntar.
//
// Además, hacemos un poll cada 30s (en lugar del default que solo chequea al
// foco de pestaña) para que dentro de ~30s del deploy todos los clientes
// activos ya tengan la nueva versión.
import { registerSW } from 'virtual:pwa-register'

// Detectar iOS PWA en modo standalone. En este modo, forzar reload del SW
// durante un permission prompt nativo (notificaciones, micrófono) produce
// parpadeo y bloquea el flujo de onboarding del guardia.
const _isIOSPWA = (
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod/.test(navigator.userAgent) &&
  typeof window !== 'undefined' &&
  window.matchMedia?.('(display-mode: standalone)').matches
)

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    if (_isIOSPWA) {
      // En iOS PWA standalone, NO forzamos reload automático. El nuevo SW
      // queda en estado "waiting" y toma control en el próximo cold start
      // de la app — el usuario al cerrar y reabrir la PWA recibe la versión
      // nueva sin parpadeo. Marcamos un flag para que la UI pueda mostrar
      // un badge "Actualización disponible" si así se desea.
      try { sessionStorage.setItem('pwa-update-pending', '1') } catch {}
      return
    }
    // Desktop / Android: skipWaiting + recarga forzada inmediata.
    updateSW(true)
  },
  onRegisteredSW(swUrl, reg) {
    if (!reg) return
    // Polling más conservador en iOS PWA (5 min vs 30s) para evitar fetch
    // storm durante permission prompts. Desktop sigue agresivo.
    const interval = _isIOSPWA ? 5 * 60_000 : 30_000
    setInterval(() => { reg.update().catch(() => {}) }, interval)
  },
})

// Sentry DSN para retaliq-frontend (proyecto JavaScript en sentry.io).
// Los DSN de Sentry son técnicamente públicos por diseño — la verificación de
// origen y rate-limit la hace Sentry server-side. Lo hardcodeamos para evitar
// pipeline build env vars; cambio futuro vía variable de entorno trivial.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
  || "https://a2c47f58b60f6e5b67f1d3ca9f197f9a@o4511508975452160.ingest.de.sentry.io/4511509001994320"

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Solo grabar sesión cuando hay error — barato y útil
        maskAllText: false,
        blockAllMedia: true,   // privacidad: NO grabar el video del CAD
      }),
    ],
    tracesSampleRate: 0.2,        // 20% transacciones de performance
    replaysSessionSampleRate: 0,  // sin replay normal
    replaysOnErrorSampleRate: 1.0, // 100% replay cuando hay error
    sendDefaultPii: true,
    // release: idealmente el SHA del commit; por ahora bundle hash
    release: import.meta.env.VITE_APP_RELEASE || `dev-${Date.now()}`,
  })
  // Exponer instancia para ErrorBoundary global
  window.__Sentry__ = Sentry
  // eslint-disable-next-line no-console
  console.log("[Sentry] iniciado · env =", import.meta.env.MODE)
}

// Error boundary wrap — captura cualquier crash de render React y lo manda
// a Sentry con stack completo + user context.
const RootApp = SENTRY_DSN
  ? Sentry.withErrorBoundary(App, {
      fallback: ({ error, resetError }) => (
        <div style={{
          padding: 40,
          textAlign: 'center',
          color: 'var(--color-text, #1A1A1A)',
          background: 'var(--color-bg, #FFF)',
          minHeight: '100vh',
        }}>
          <h1 style={{ marginBottom: 16 }}>Algo se rompió</h1>
          <p style={{ marginBottom: 24, opacity: 0.7 }}>
            El error fue reportado automáticamente. Probá recargar.
          </p>
          <button onClick={resetError} style={{
            padding: '10px 20px',
            background: 'var(--color-primary, #E60023)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
          }}>
            Recargar app
          </button>
          {import.meta.env.DEV && (
            <pre style={{
              marginTop: 24,
              padding: 16,
              background: '#f5f5f5',
              textAlign: 'left',
              borderRadius: 4,
              fontSize: 11,
              overflow: 'auto',
              maxWidth: 720,
              margin: '24px auto 0',
            }}>
              {error?.message}{"\n"}{error?.stack}
            </pre>
          )}
        </div>
      ),
    })
  : App

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/incident/:id" element={<IncidentView />} />
        <Route path="/guard/*"      element={<GuardView />} />
        <Route path="/*"            element={<RootApp />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>
)
